import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import type { PageHeaderBreadcrumb } from '@/components/ui/PageHeader';

/**
 * Breadcrumb configuration for different routes
 */
interface BreadcrumbConfig {
  path: string;
  label: string;
  parent?: string;
  dynamic?: boolean;
  icon?: React.ReactNode;
}

/**
 * Static breadcrumb configuration
 */
const BREADCRUMB_CONFIG: Record<string, BreadcrumbConfig> = {
  '/': {
    path: '/',
    label: 'Dashboard',
  },
  '/dashboard': {
    path: '/dashboard',
    label: 'Dashboard',
  },
  '/transactions': {
    path: '/transactions',
    label: 'Transactions',
    parent: '/dashboard',
  },
  '/transactions/view': {
    path: '/transactions/view',
    label: 'View Transaction',
    parent: '/transactions',
    dynamic: true,
  },
  '/transactions/create': {
    path: '/transactions/create',
    label: 'Create Transaction',
    parent: '/transactions',
  },
  '/transactions/export': {
    path: '/transactions/export',
    label: 'Export Transactions',
    parent: '/transactions',
  },
  '/transactions/reports': {
    path: '/transactions/reports',
    label: 'Transaction Reports',
    parent: '/transactions',
  },
  '/transactions/settings': {
    path: '/transactions/settings',
    label: 'Transaction Settings',
    parent: '/transactions',
  },
};

/**
 * Dynamic breadcrumb generation based on current route and context
 */
export const useBreadcrumbs = (
  customBreadcrumbs?: PageHeaderBreadcrumb[],
  context?: {
    transactionId?: string;
    merchantTransactionId?: string;
    paymentGateway?: string;
  }
) => {
  const location = useLocation();
  const navigate = useNavigate();

  const breadcrumbs: PageHeaderBreadcrumb[] = useMemo(() => {
    // If custom breadcrumbs are provided, use them
    if (customBreadcrumbs) {
      return customBreadcrumbs.map(breadcrumb => ({
        ...breadcrumb,
        onClick:
          breadcrumb.onClick || (breadcrumb.href ? () => navigate(breadcrumb.href!) : undefined),
      }));
    }

    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    // Find the current route configuration
    let currentConfig = BREADCRUMB_CONFIG[currentPath];

    // Handle dynamic routes
    if (!currentConfig) {
      // Check for partial matches (dynamic routes)
      const matchingPath = Object.keys(BREADCRUMB_CONFIG).find(path => {
        if (path.includes(':') || BREADCRUMB_CONFIG[path].dynamic) {
          const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pathPattern}$`);
          return regex.test(currentPath);
        }
        return false;
      });

      if (matchingPath) {
        currentConfig = BREADCRUMB_CONFIG[matchingPath];
      }
    }

    if (!currentConfig) {
      // Fallback for unknown routes
      return [
        {
          label: 'Unknown Page',
          active: true,
        },
      ];
    }

    // Build breadcrumb chain
    const breadcrumbChain: PageHeaderBreadcrumb[] = [];
    let config: BreadcrumbConfig | undefined = currentConfig;

    // Build the chain by traversing parents
    while (config) {
      const isActive =
        config.path === currentPath ||
        (config.dynamic && currentPath.startsWith(config.path.replace(/:[^/]+.*$/, '')));

      let label = config.label;

      // Handle dynamic labels based on context
      if (config.dynamic && context) {
        if (config.path.includes('/transactions/view') && context.transactionId) {
          label = `Transaction ${context.transactionId}`;
        } else if (context.merchantTransactionId) {
          label = `${config.label} - ${context.merchantTransactionId}`;
        }
      }

      // Add query parameters to label if relevant
      if (searchParams.has('status')) {
        const status = searchParams.get('status');
        if (config.path === '/transactions' && status) {
          label += ` (${status.toUpperCase()})`;
        }
      }

      breadcrumbChain.unshift({
        label,
        href: config.path,
        onClick: () => navigate(config!.path),
        active: isActive,
      });

      // Move to parent
      config = config.parent ? BREADCRUMB_CONFIG[config.parent] : undefined;
    }

    return breadcrumbChain;
  }, [location.pathname, location.search, customBreadcrumbs, context, navigate]);

  return breadcrumbs;
};

/**
 * Hook to get breadcrumbs specifically for transaction pages
 */
export const useTransactionBreadcrumbs = (
  transactionId?: string,
  merchantTransactionId?: string,
  paymentGateway?: string
) => {
  return useBreadcrumbs(undefined, {
    transactionId,
    merchantTransactionId,
    paymentGateway,
  });
};

/**
 * Utility function to create custom breadcrumbs
 */
export const createBreadcrumbs = (
  items: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
    active?: boolean;
  }>
): PageHeaderBreadcrumb[] => {
  return items.map((item, index) => ({
    ...item,
    active: item.active ?? index === items.length - 1,
  }));
};

/**
 * Pre-defined breadcrumb sets for common transaction scenarios
 */
export const TRANSACTION_BREADCRUMBS = {
  list: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', active: true },
    ]),

  view: (transactionId: string): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', href: '/transactions' },
      { label: `Transaction ${transactionId}`, active: true },
    ]),

  create: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', href: '/transactions' },
      { label: 'Create Transaction', active: true },
    ]),

  reports: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', href: '/transactions' },
      { label: 'Reports', active: true },
    ]),

  export: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', href: '/transactions' },
      { label: 'Export Data', active: true },
    ]),
};

export default useBreadcrumbs;
